import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FaEye, FaCheckCircle, FaTimes, FaSpinner, FaMapMarkerAlt } from 'react-icons/fa';
import ClientLayout from '../layouts/ClientLayout';
import Modal from '../components/Modal';
import { clientQuotes } from '../api';
import { effectiveQuoteStatus, EXPIRED_META } from '../utils/quoteStatus';

const STATUS_META = {
  draft: { label: 'Draft', color: '#64748b', bg: '#f1f5f9' },
  client_request: { label: 'Pending Review', color: '#b45309', bg: '#fffbeb' },
  sent: { label: 'Sent', color: '#2563eb', bg: '#eff6ff' },
  client_accepted: { label: 'Accepted', color: '#16a34a', bg: '#f0fdf4' },
  approved: { label: 'Approved', color: '#16a34a', bg: '#f0fdf4' },
  rejected: { label: 'Rejected', color: '#dc2626', bg: '#fef2f2' },
  cancelled: { label: 'Cancelled', color: '#6b7280', bg: '#f3f4f6' },
  credited: { label: 'Credited', color: '#6b7280', bg: '#f3f4f6' },
  // Voided = the invoice was cancelled before any payment was received. Never "Credited":
  // nothing came in, so nothing is owed back.
  void: { label: 'Voided', color: '#6b7280', bg: '#f3f4f6' },
  // Derived, not stored - shown when a sent quote is past its expiry date.
  expired: EXPIRED_META
};

const TYPE_LABELS = {
  minerals: '⛏️ Minerals', agri: '🌾 Agri', abnormal: '🚛 Abnormal',
  container: '📦 Container', general: '📋 General'
};

const fmtMoney = (n) => 'R ' + (Number(n) || 0).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-ZA', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

function ClientQuotesPage({ onLogout }) {
  const navigate = useNavigate();
  const [user, setUser] = useState({});
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [acceptTarget, setAcceptTarget] = useState(null);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    setUser(userData);
    fetchQuotes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchQuotes = async () => {
    setLoading(true);
    try {
      const res = await clientQuotes.getAll();
      if (res.success) setQuotes(res.quotes || []);
      else toast.error(res.error || 'Failed to load quotes');
    } catch (err) {
      console.error('Error fetching quotes:', err);
      toast.error('Failed to load quotes');
    } finally {
      setLoading(false);
    }
  };

  // A quote opens on its own page (ClientQuoteDetail) — no more cramped modal.
  const goToQuote = (q) => navigate(`/client-quotes/${q.id}`);

  const confirmAccept = async () => {
    if (!acceptTarget) return;
    setBusy(true);
    try {
      const res = await clientQuotes.accept(acceptTarget.id);
      if (res.success) {
        toast.success(`✅ Quote ${acceptTarget.quote_number} accepted. We'll confirm your job shortly.`);
        setAcceptTarget(null);
        fetchQuotes();
      } else {
        toast.error(res.error || 'Failed to accept quote');
      }
    } catch {
      toast.error('Failed to accept quote');
    } finally {
      setBusy(false);
    }
  };

  const confirmReject = async () => {
    if (!rejectTarget) return;
    setBusy(true);
    try {
      const res = await clientQuotes.reject(rejectTarget.id, { reason: rejectReason });
      if (res.success) {
        toast.success(`Quote ${rejectTarget.quote_number} rejected.`);
        setRejectTarget(null);
        setRejectReason('');
        fetchQuotes();
      } else {
        toast.error(res.error || 'Failed to reject quote');
      }
    } catch {
      toast.error('Failed to reject quote');
    } finally {
      setBusy(false);
    }
  };

  const filtered = filter === 'all' ? quotes : quotes.filter(q => effectiveQuoteStatus(q) === filter);

  return (
    <ClientLayout activeTab="quotes" onLogout={onLogout} user={user}>
      <div className="flex flex-wrap justify-between items-center gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: '#0A1929' }}>📄 My Quotes</h1>
          <p className="text-sm" style={{ color: '#4A5568' }}>Track and respond to your quotations</p>
        </div>
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="px-3 py-2 border rounded-lg text-sm bg-white"
          style={{ borderColor: '#E8ECEF', color: '#0A1929' }}
        >
          <option value="all">All Statuses</option>
          {Object.entries(STATUS_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="text-4xl mb-4 animate-spin">🔄</div>
            <p className="text-gray-500">Loading your quotes...</p>
          </div>
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-xl border p-16 text-center" style={{ borderColor: '#E8ECEF' }}>
          <div className="text-5xl mb-4">📭</div>
          <h3 className="text-lg font-semibold mb-2" style={{ color: '#0A1929' }}>No quotes found</h3>
          <p className="text-sm text-gray-500 mb-6">
            {filter !== 'all' ? 'No quotes match the selected status.' : 'When we send you a quote it will appear here. You can also request one from the New Job page.'}
          </p>
          {filter === 'all' && (
            <Link to="/client-new-job" className="inline-block px-6 py-2.5 rounded-lg text-white text-sm font-semibold" style={{ backgroundColor: '#00A86B' }}>
              Request a Quote
            </Link>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-xl border overflow-hidden" style={{ borderColor: '#E8ECEF' }}>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="border-b bg-gray-50" style={{ borderColor: '#E8ECEF' }}>
                <tr className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  <th className="px-4 py-3">Quote #</th>
                  <th className="px-4 py-3">Type</th>
                  <th className="px-4 py-3">Route</th>
                  <th className="px-4 py-3">Owner</th>
                  <th className="px-4 py-3">Date</th>
                  <th className="px-4 py-3 text-right">Total (incl. VAT)</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(q => {
                  const meta = STATUS_META[effectiveQuoteStatus(q)] || STATUS_META.draft;
                  return (
                    <tr key={q.id} className="border-b hover:bg-gray-50 transition-colors" style={{ borderColor: '#F1F5F9' }}>
                      <td className="px-4 py-3">
                        <button onClick={() => goToQuote(q)} className="font-semibold hover:underline text-left" style={{ color: '#0A1929' }}>
                          {q.quote_number}
                        </button>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">{TYPE_LABELS[q.quote_type] || q.quote_type}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {q.origin && q.destination ? (
                          <div className="flex flex-col gap-0.5">
                            <span className="flex items-center gap-1">
                              <FaMapMarkerAlt size={11} style={{ color: '#00A86B' }} />
                              <span className="max-w-[190px] truncate" title={q.origin}>{q.origin}</span>
                              <span className="text-gray-400">→</span>
                              <FaMapMarkerAlt size={11} style={{ color: '#2563eb' }} />
                              <span className="max-w-[190px] truncate" title={q.destination}>{q.destination}</span>
                            </span>
                            {(q.distance_km || q.cross_border) && (
                              <span className="text-xs text-gray-400">
                                {q.distance_km ? `${q.distance_km} km` : ''}
                                {q.cross_border ? `${q.distance_km ? ' • ' : ''}Cross-border` : ''}
                              </span>
                            )}
                          </div>
                        ) : '—'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600" title={q.contact_email || q.customer?.contact_email || ''}>
                        {q.contact_person || q.customer?.contact_person || '—'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">{fmtDate(q.created_at)}</td>
                      <td className="px-4 py-3 text-sm font-semibold text-right" style={{ color: '#0A1929' }}>{fmtMoney(q.grand_total || q.total_amount)}</td>
                      <td className="px-4 py-3">
                        <span className="px-2.5 py-1 rounded-full text-xs font-semibold" style={{ color: meta.color, backgroundColor: meta.bg }}>
                          {meta.label}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-2">
                          <button onClick={() => goToQuote(q)} title="View" className="w-8 h-8 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-blue-600 flex items-center justify-center">
                            <FaEye size={14} />
                          </button>
                          {q.status === 'sent' && effectiveQuoteStatus(q) !== 'expired' && (
                            <>
                              <button onClick={() => setAcceptTarget(q)} title="Accept" className="w-8 h-8 rounded-lg hover:bg-green-50 text-gray-400 hover:text-green-600 flex items-center justify-center">
                                <FaCheckCircle size={15} />
                              </button>
                              <button onClick={() => setRejectTarget(q)} title="Reject" className="w-8 h-8 rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-600 flex items-center justify-center">
                                <FaTimes size={15} />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Quote details now open on their own page — see /client-quotes/:id */}

      {/* ── Accept Confirm Modal ── */}
      <Modal isOpen={!!acceptTarget} onClose={() => { if (!busy) setAcceptTarget(null); }} title="Accept Quote">
        <p className="text-gray-600 text-sm mb-6">
          Accept quote <strong>{acceptTarget?.quote_number}</strong>?
          Our team will confirm your job shortly after you accept.
        </p>
        <div className="flex justify-end gap-3">
          <button onClick={() => setAcceptTarget(null)} disabled={busy} className="px-4 py-2 rounded-lg border border-gray-200 text-sm hover:bg-gray-50">
            Cancel
          </button>
          <button onClick={confirmAccept} disabled={busy} className="px-6 py-2 rounded-lg text-white text-sm font-semibold flex items-center gap-2 disabled:opacity-50" style={{ backgroundColor: '#00A86B' }}>
            {busy ? <FaSpinner className="animate-spin" /> : <FaCheckCircle />} Accept
          </button>
        </div>
      </Modal>

      {/* ── Reject Modal ── */}
      <Modal isOpen={!!rejectTarget} onClose={() => { if (!busy) { setRejectTarget(null); setRejectReason(''); } }} title="Reject Quote">
        <p className="text-gray-600 text-sm mb-4">
          Reject quote <strong>{rejectTarget?.quote_number}</strong>? Please let us know why so we can improve (optional).
        </p>
        <textarea
          value={rejectReason}
          onChange={(e) => setRejectReason(e.target.value)}
          rows={3}
          placeholder="Reason (optional)..."
          className="w-full px-3 py-2 border rounded-lg text-sm mb-6 focus:outline-none"
          style={{ borderColor: '#E8ECEF' }}
        />
        <div className="flex justify-end gap-3">
          <button onClick={() => { setRejectTarget(null); setRejectReason(''); }} disabled={busy} className="px-4 py-2 rounded-lg border border-gray-200 text-sm hover:bg-gray-50">
            Cancel
          </button>
          <button onClick={confirmReject} disabled={busy} className="px-6 py-2 rounded-lg text-white text-sm font-semibold flex items-center gap-2 disabled:opacity-50" style={{ backgroundColor: '#dc2626' }}>
            {busy ? <FaSpinner className="animate-spin" /> : <FaTimes />} Reject
          </button>
        </div>
      </Modal>
    </ClientLayout>
  );
}

export default ClientQuotesPage;
